import { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Button,
  Center,
  Flex,
  Loader,
  Modal,
  NativeSelect,
  Paper,
  Stack,
  Text,
  Title,
  ActionIcon,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconGripVertical, IconPencil, IconTrash, IconPlus, IconDeviceFloppy } from '@tabler/icons-react';
import { trpc } from '../lib/trpc';
import RichTextEditor from '../components/RichTextEditor';

// ─── Local types ─────────────────────────────────────────────────────────────

interface PageRow {
  id: number;
  title: string | null;
  navtitle: string | null;
  depth: number;
  lft: number;
  rgt: number;
}

interface ConfigRow {
  id: number;
  contentId: number;
  title: string | null;
  value: string | null;
}

interface ComponentRow {
  id: number;
  pageId: number | null;
  componentId: number | null;
  block: string | null;
  sort: number | null;
  editable: boolean | null;
  locked: boolean | null;
  lockedDelete: boolean | null;
  componentTitle: string | null;
  componentModule: string | null;
  componentClassname: string | null;
  configs: ConfigRow[];
}

function getTextValue(item: ComponentRow): string {
  const textConfig = item.configs.find((c) => c.title === 'text');
  return textConfig?.value ?? '';
}

// ─── Text Editor Modal ────────────────────────────────────────────────────────

interface TextEditorModalProps {
  contentId: number;
  initialValue: string;
  onClose: () => void;
}

function TextEditorModal({ contentId, initialValue, onClose }: TextEditorModalProps) {
  const [value, setValue] = useState(initialValue);
  const utils = trpc.useUtils();

  const updateConfig = trpc.admin.page.updateConfig.useMutation({
    onSuccess: () => {
      void utils.admin.page.getPageComponents.invalidate();
      onClose();
    },
  });

  return (
    <Modal opened onClose={onClose} title="Edit Text" size="xl">
      <Stack>
        <Box style={{ overflow: 'auto' }}>
          <RichTextEditor value={value} onChange={setValue} />
        </Box>
        <Flex gap="sm" justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={() => updateConfig.mutate({ contentId, title: 'text', value })}
            loading={updateConfig.isPending}
          >
            Save
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
}

// ─── Sortable Component Row ───────────────────────────────────────────────────

interface SortableComponentRowProps {
  item: ComponentRow;
  onEdit: (item: ComponentRow) => void;
  onRemove: (id: number) => void;
}

function SortableComponentRow({ item, onEdit, onRemove }: SortableComponentRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  return (
    <Paper
      ref={setNodeRef}
      withBorder
      mb="xs"
      bg={isDragging ? 'gray.1' : undefined}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      px="sm"
      py="xs"
    >
      <Flex align="center" gap="sm">
        <Box
          {...attributes}
          {...listeners}
          style={{ cursor: 'grab', userSelect: 'none', lineHeight: 1 }}
          c="dimmed"
        >
          <IconGripVertical size={18} />
        </Box>
        <Box style={{ flex: 1 }}>
          <Text size="sm" fw={500} component="span">
            {item.componentTitle ?? '(unknown)'}
          </Text>
          {item.block && (
            <Text size="xs" c="dimmed" component="span" ml="xs">
              {item.block}
            </Text>
          )}
        </Box>
        {item.editable && (
          <ActionIcon variant="subtle" onClick={() => onEdit(item)} title="Edit Text">
            <IconPencil size={16} />
          </ActionIcon>
        )}
        {!item.lockedDelete && (
          <ActionIcon variant="subtle" color="red" onClick={() => onRemove(item.id)} title="Remove">
            <IconTrash size={16} />
          </ActionIcon>
        )}
      </Flex>
    </Paper>
  );
}

// ─── Component Manager ────────────────────────────────────────────────────────

interface ComponentManagerProps {
  pageId: number;
}

function ComponentManager({ pageId }: ComponentManagerProps) {
  const utils = trpc.useUtils();
  const [editingItem, setEditingItem] = useState<ComponentRow | null>(null);
  const [selectedComponentId, setSelectedComponentId] = useState('');
  const [localItems, setLocalItems] = useState<ComponentRow[]>([]);

  const { data: serverComponents } = trpc.admin.page.getPageComponents.useQuery({ pageId });
  const { data: availableComponents } = trpc.admin.page.listComponents.useQuery();

  useEffect(() => {
    if (serverComponents) {
      setLocalItems(serverComponents as unknown as ComponentRow[]);
    }
  }, [serverComponents]);

  const addComponent = trpc.admin.page.addComponent.useMutation({
    onSuccess: () => {
      void utils.admin.page.getPageComponents.invalidate({ pageId });
      setSelectedComponentId('');
    },
  });

  const removeComponent = trpc.admin.page.removeComponent.useMutation({
    onSuccess: () => void utils.admin.page.getPageComponents.invalidate({ pageId }),
  });

  const reorderComponents = trpc.admin.page.reorderComponents.useMutation();

  const sensors = useSensors(useSensor(PointerSensor));

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localItems.findIndex((item) => item.id === Number(active.id));
    const newIndex = localItems.findIndex((item) => item.id === Number(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(localItems, oldIndex, newIndex);
    setLocalItems(reordered);

    reorderComponents.mutate({
      items: reordered.map((item, index) => ({ id: item.id, sort: index + 1 })),
    });
  }

  function handleRemove(id: number) {
    modals.openConfirmModal({
      title: 'Remove component',
      children: <Text size="sm">Remove this component from the page?</Text>,
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => removeComponent.mutate({ id }),
    });
  }

  function handleAdd() {
    const componentId = Number(selectedComponentId);
    if (Number.isNaN(componentId) || !componentId) return;
    addComponent.mutate({ pageId, componentId });
  }

  const componentOptions = [
    { value: '', label: 'Add component…' },
    ...(availableComponents ?? []).map((c) => ({ value: String(c.id), label: c.title ?? String(c.id) })),
  ];

  return (
    <Stack>
      <Flex align="center" justify="space-between">
        <Title order={3}>Components</Title>
        <Flex gap="sm" align="center">
          <NativeSelect
            value={selectedComponentId}
            onChange={(e) => setSelectedComponentId(e.currentTarget.value)}
            data={componentOptions}
          />
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAdd}
            disabled={!selectedComponentId}
          >
            Add
          </Button>
        </Flex>
      </Flex>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={localItems.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          {localItems.map((item) => (
            <SortableComponentRow
              key={item.id}
              item={item}
              onEdit={setEditingItem}
              onRemove={handleRemove}
            />
          ))}
        </SortableContext>
      </DndContext>

      {localItems.length === 0 && (
        <Text c="dimmed" py="sm">
          No components on this page.
        </Text>
      )}

      {editingItem && (
        <TextEditorModal
          contentId={editingItem.id}
          initialValue={getTextValue(editingItem)}
          onClose={() => setEditingItem(null)}
        />
      )}
    </Stack>
  );
}

// ─── Page Manager ─────────────────────────────────────────────────────────────

export default function PageManager() {
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);

  const { data: pages, isLoading } = trpc.admin.page.getTree.useQuery();

  if (isLoading) return <Center h="60vh"><Loader /></Center>;

  const pageList = (pages ?? []) as PageRow[];

  return (
    <Flex gap={0} style={{ minHeight: '60vh' }}>
      {/* Left panel — page tree */}
      <Box
        style={{
          width: 280,
          flexShrink: 0,
          borderRight: '1px solid var(--mantine-color-gray-3)',
          overflowY: 'auto',
        }}
        pr="md"
      >
        <Title order={3} mb="sm">
          Pages
        </Title>
        {pageList.map((p) => (
          <Box
            key={p.id}
            onClick={() => setSelectedPageId(p.id)}
            py="xs"
            pr="sm"
            mb={2}
            bg={selectedPageId === p.id ? 'blue.0' : 'transparent'}
            style={{
              paddingLeft: 8 + p.depth * 16,
              borderRadius: 'var(--mantine-radius-sm)',
              cursor: 'pointer',
            }}
          >
            <Text size="sm" fw={p.depth === 0 ? 600 : 400}>
              {p.navtitle ?? p.title ?? `Page #${p.id}`}
            </Text>
          </Box>
        ))}
      </Box>

      {/* Right panel — component manager */}
      <Box style={{ flex: 1 }} pl="lg">
        {selectedPageId ? (
          <ComponentManager key={selectedPageId} pageId={selectedPageId} />
        ) : (
          <Text c="dimmed" pt="xs">
            Select a page to manage its components.
          </Text>
        )}
      </Box>
    </Flex>
  );
}
